import { useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { useAnalyticsOrgLocations, useAverageProductionAmount } from '@frontend/analytics-shared';
import { CategoryBarChartData, CategoryBarChartProps, Chart, CustomColumnRendererParams } from '@frontend/charts';
import { i18next, useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { useSearch } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { useFetchMessagingROIOOverview } from '../../../hooks/use-fetch-messaging-roi-overview';

const colors = {
  existingPatients: theme.colors.primary20,
  newPatients: theme.colors.indigo50,
  unscheduledPatients: theme.colors.warning10,
};

const strokeColors = {
  existingPatients: theme.colors.primary40,
  newPatients: theme.colors.indigo60,
  unscheduledPatients: theme.colors.warning20,
};

const labels = {
  existingPatients: i18next.t('Existing Patients', { ns: 'analytics' }),
  groupNameHeader: i18next.t('Location', { ns: 'analytics' }),
  newPatients: i18next.t('New Patients', { ns: 'analytics' }),
  unscheduledPatients: i18next.t('Unscheduled Leads', { ns: 'analytics' }),
};
interface MultiComparisonChartProps extends ReturnType<typeof useFetchMessagingROIOOverview> {
  title: string;
}

export const MultiComparisonChart = ({
  aggregated,
  isLoading,
  locationsData = {},
  title,
}: MultiComparisonChartProps) => {
  const { t } = useTranslation('analytics');
  const search = useSearch({ strict: false }) ?? { view: 'leadsContacted' };
  const { locationNames } = useAnalyticsOrgLocations({
    module: 'ROI',
  });
  const { averageProductionAmount } = useAverageProductionAmount();

  const processedData = useMemo(() => {
    const chartData: CategoryBarChartData = {};
    const customColumns: CategoryBarChartProps['customColumns'] = [];
    let legendsValues: Record<string, string> = {};

    switch (search.view) {
      case 'leadsContacted':
        Object.values(locationsData).forEach(({ locationId, leadsContacted }) => {
          if (!locationId) {
            return;
          }

          const newPatients = leadsContacted?.newPatientsCounts || 0;
          const existingPatients = leadsContacted?.existingPatientsCounts || 0;
          const unscheduledPatients = leadsContacted?.unscheduledPatientsCounts || 0;

          chartData[locationId] = {
            newPatients,
            unscheduledPatients,
            existingPatients,
          };

          customColumns[0] = {
            columnHeaderId: 'patientsContacted',
            columnHeaderLabel: t('Patients Contacted'),
            data: {
              ...(customColumns[0]?.data || {}),
              [locationId]: newPatients + existingPatients + unscheduledPatients,
            },
          };
        });

        legendsValues = {
          newPatients: formatters.value(aggregated?.leadsContacted?.newPatientsCounts || 0),
          existingPatients: formatters.value(aggregated?.leadsContacted?.existingPatientsCounts || 0),
          unscheduledPatients: formatters.value(aggregated?.leadsContacted?.unscheduledPatientsCounts || 0),
        };
        break;

      case 'patientsScheduled':
        Object.values(locationsData).forEach(({ locationId, patientsScheduled }) => {
          if (!locationId) {
            return;
          }

          const newPatients = patientsScheduled?.newPatientsCounts || 0;
          const existingPatients = patientsScheduled?.existingPatientsCounts || 0;

          chartData[locationId] = {
            newPatients,
            existingPatients,
          };

          customColumns[0] = {
            columnHeaderId: 'patientsScheduled',
            columnHeaderLabel: t('Patients Scheduled'),
            data: {
              ...(customColumns[0]?.data || {}),
              [locationId]: newPatients + existingPatients,
            },
          };
        });

        legendsValues = {
          newPatients: formatters.value(aggregated?.patientsScheduled?.newPatientsCounts || 0),
          existingPatients: formatters.value(aggregated?.patientsScheduled?.existingPatientsCounts || 0),
        };
        break;

      case 'revenueGenerated':
        Object.values(locationsData).forEach(({ locationId, appointmentsCompleted }) => {
          if (!locationId) {
            return;
          }

          const newPatients = (appointmentsCompleted?.newPatientsApptsCounts || 0) * averageProductionAmount.value;
          const existingPatients =
            (appointmentsCompleted?.existingPatientsApptsCounts || 0) * averageProductionAmount.value;

          chartData[locationId] = {
            newPatients,
            existingPatients,
          };

          customColumns[0] = {
            columnHeaderId: 'revenueGenerated',
            columnHeaderLabel: t('Revenue Generated'),
            data: {
              ...(customColumns[0]?.data || {}),
              [locationId]: newPatients + existingPatients,
            },
          };
        });

        legendsValues = {
          newPatients: formatters.currency(
            (aggregated?.appointmentsCompleted?.newPatientsApptsCounts || 0) * averageProductionAmount.value
          ),
          existingPatients: formatters.currency(
            (aggregated?.appointmentsCompleted?.existingPatientsApptsCounts || 0) * averageProductionAmount.value
          ),
        };
        break;
    }

    return {
      aggregated,
      chartData,
      customColumns,
      legendsValues,
    };
  }, [locationsData, search.view, aggregated, t, averageProductionAmount]);

  const renderCustomColumnValue = useCallback(
    ({ groupName, value }: CustomColumnRendererParams) => {
      const locationData = locationsData?.[groupName];

      switch (search.view) {
        case 'leadsContacted': {
          const leadsContacted = locationData.leadsContacted;
          return (
            <CustomColumnRenderer
              mainText={t('Patients')}
              subText={t('{{count}} Messages sent', { count: leadsContacted?.smsCount || 0 })}
              value={value}
            />
          );
        }

        case 'patientsScheduled': {
          const leadsContacted = locationData?.patientsScheduled;
          return (
            <CustomColumnRenderer
              mainText={t('Patients')}
              subText={t('{{count}} Appointments', { count: leadsContacted?.appointmentsCount || 0 })}
              value={value}
            />
          );
        }

        case 'revenueGenerated':
          return (
            <Text style={{ fontSize: theme.fontSize(24) }} weight='bold'>
              {value}
            </Text>
          );
      }

      return null;
    },
    [search.view, locationsData, t]
  );

  return (
    <Chart
      colors={colors}
      isLoading={isLoading}
      labels={{ ...labels, ...locationNames }}
      strokeColors={strokeColors}
      style={{ marginBottom: 0 }}
    >
      <Chart.Header title={title} />
      <Chart.CategoryBarChart
        appearance={{
          alwaysApplyBarBorders: true,
          barBorderWidth: 1,
          barSize: 40,
          collectiveTooltip: true,
          formatters: search.view === 'revenueGenerated' ? (v) => formatters.currency(v) : formatters.value,
          groupsGap: 40,
          mode: 'stacked',
        }}
        customColumns={processedData.customColumns}
        customColumnValueRenderer={renderCustomColumnValue}
        data={processedData.chartData}
        highestValueConfig={{
          color: theme.colors.warning40,
          text: t('Highest Conversion'),
        }}
        sortConfig={{
          allowUserToSort: true,
        }}
        trackingIdBase='messaging-roi-multi-comparison'
      >
        <Chart.Legends values={processedData.legendsValues} />
      </Chart.CategoryBarChart>
    </Chart>
  );
};

interface CustomColumnRendererProps {
  mainText: string;
  subText: string;
  value: number | string;
}

const CustomColumnRenderer = ({ mainText, subText, value }: CustomColumnRendererProps) => {
  return (
    <div>
      <div
        css={css`
          align-items: baseline;
          display: flex;
          gap: ${theme.spacing(0.5)};
        `}
      >
        <Text style={{ fontSize: theme.fontSize(20) }} weight='bold'>
          {value}
        </Text>
        <Text size='small'>{mainText}</Text>
      </div>
      <Text color='subdued' size='small'>
        {subText}
      </Text>
    </div>
  );
};
